import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ClockIcon, FlameIcon, LeafIcon, PlusIcon } from 'lucide-react';
import { Dish, money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { DishConfigurator } from './DishConfigurator';
import { ListingDetailModal } from '../ListingDetailModal';
import type { ListingCardProps } from '../ListingCard';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';

/**
 * A food result is not a vehicle result. Nobody orders lunch off a spec grid,
 * and a "94% match" badge on a burger is noise — so this card leads with the
 * food itself, says what is in it, and gets straight to ordering. The one piece
 * of metadata that genuinely changes a lunch decision (how long it takes) sits
 * on the photo; everything else is deferred to the order sheet.
 */
export function DishCard(props: ListingCardProps & {
  dish: Dish;
}) {
  const {
    id,
    title,
    location,
    images,
    dish,
    compact
  } = props;
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen,
    lines
  } = useCart();
  const [configuring, setConfiguring] = useState(false);
  const [details, setDetails] = useState(false);
  /** What's in the order from this card, so the card reflects the cart. */
  const inOrder = lines.filter(l => l.listingId === id);
  const orderedQty = inOrder.reduce((n, l) => n + l.qty, 0);
  const orderedTotal = inOrder.reduce((n, l) => n + l.unitPrice * l.qty, 0);
  const [kitchen, area] = location.split(' · ');
  const from = Math.min(...dish.sizes.map(s => s.price));
  /** What's in it, in the kitchen's own words — the real food-card subtitle. */
  const madeWith = dish.included.map(i => i.label).join(', ');
  const vegetarian = !dish.allergens.includes('Meat') && /halloumi|veg|falafel|truffle fries/i.test(title);
  return <>
      <motion.div whileHover={{
      y: -4
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="transform-gpu bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-soft hover:shadow-medium transition-shadow duration-200 ease-out flex flex-col h-full group" data-id="element-2101" data-mp="3f2wpg29oful7">
        {/* Same square frame as every other result card, so a mixed row of
            food, clothes and fragrances lines up. The kitchen and the one
            number that changes a lunch decision ride on the photo. */}
        <div className={`relative overflow-hidden bg-slate-100 ${compact ? 'aspect-[16/10]' : 'aspect-square'}`} data-id="element-2102" data-mp="dmvo71q0om40">
          <img src={images[0]} alt={title} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" data-id="element-2103" data-mp="ilp1at1e6b3n" />
          {/* Inside one brand's own menu every card carries the same kitchen,
              so the badge is noise — the header already said who this is. */}
          {!compact && <div className="absolute top-2 right-2" data-id="element-2104" data-mp="16k6cs7aj0z3l">
              <MerchantBadge merchant={merchantFor(kitchen, 'Kitchen')} variant="frosted" data-id="element-2105" data-mp="11rwm2p1l7v09" />
            </div>}
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5" data-id="element-2106" data-mp="srgmvrrvna17">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 backdrop-blur-sm text-[10.5px] font-bold text-dark-navy shadow-sm" data-id="element-2107" data-mp="spdn4g0xa2ja">
              <ClockIcon className="w-3 h-3 text-slate-400" data-id="element-2108" data-mp="1lh707yloq7w9" />
              {dish.prepTime}
            </span>
            {(dish.spiceLevel || vegetarian) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 backdrop-blur-sm text-[10.5px] font-bold text-slate-600 shadow-sm" data-id="element-2109" data-mp="1bi839daa80p3">
                {vegetarian ? <>
                    <LeafIcon className="w-3 h-3 text-success" data-id="element-2110" data-mp="2jdke5n8nkl" />
                    Vegetarian
                  </> : <>
                    <FlameIcon className="w-3 h-3 text-orange-500" data-id="element-2111" data-mp="1sq7d561gtz4p" />
                    {dish.spiceLevel}
                  </>}
              </span>}
          </div>
        </div>

        <div className="p-3 flex flex-col flex-1" data-id="element-2112" data-mp="1jxx3pqye9xoi">
          <div className="flex items-start justify-between gap-2" data-id="element-2113" data-mp="1jyf6z7jh5c5x">
            <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1 min-w-0" data-id="element-2114" data-mp="27mycjfb53aub">
              {title}
            </h3>
            <span className="font-display font-bold text-[15px] text-dark-navy leading-snug flex-shrink-0" data-id="element-2115" data-mp="2bmz3fzffdei9">
              {orderedQty > 0 ? money(orderedTotal) : <>
                  <span className="text-[11px] font-semibold text-slate-400" data-id="element-2116" data-mp="ma546qbv6kdr">
                    from{' '}
                  </span>
                  {money(from)}
                </>}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-2117" data-mp="joxla3345na7">
            {madeWith}
          </p>

          <div className="mt-auto pt-2.5" data-id="element-2118" data-mp="epksl1pk9lzp">
            {orderedQty > 0 ? <button onClick={() => setOpen(true)} className="w-full text-left mb-1.5 text-[11px] font-bold text-success hover:underline truncate" data-id="element-2119" data-mp="sqx5f49fd7hd">
                {orderedQty} in your order · {inOrder[0]?.summary}
              </button> : !compact && area && <p className="text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-2120" data-mp="3s3fq7vxrcmpl">
                  {area}
                </p>}
            <div className="flex items-center gap-1.5" data-id="element-2121" data-mp="50ko52sahye4">
              <button onClick={() => requireAuth(() => setConfiguring(true))} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-2122" data-mp="4yztze9w0hgs">
                <PlusIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2123" data-mp="1hd81a9mbrxs8" />
                <span className="truncate" data-id="element-2124" data-mp="1hbn4edcjg2r7">
                  {orderedQty > 0 ? 'Add another' : 'Choose options'}
                </span>
              </button>
              <button onClick={() => requireAuth(() => setDetails(true))} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2125" data-mp="1r3kne69sfz0h">
                Details
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Portalled to the body — the card itself is a transformed, scrolling
          element, so a sheet rendered inside it would be trapped by it. */}
      {configuring && createPortal(<DishConfigurator title={title} kitchen={kitchen} image={images[0]} images={images} dish={dish} onClose={() => setConfiguring(false)} onAdd={(summary, unitPrice, qty) => {
      add({
        listingId: id,
        merchant: kitchen,
        title,
        image: images[0],
        summary,
        unitPrice,
        qty
      });
      setConfiguring(false);
    }} data-id="element-2126" data-mp="2itfn8wv9v73e" />, document.body)}

      <ListingDetailModal listing={{
      ...props,
      specs: [{
        label: 'Ready in',
        value: dish.prepTime
      }, {
        label: 'Sizes',
        value: dish.sizes.map(s => s.label).join(', ')
      }, {
        label: 'Includes',
        value: dish.included.map(i => i.label).join(', ')
      }, {
        label: 'Allergens',
        value: dish.allergens.join(', ') || 'None listed'
      }]
    }} open={details} onClose={() => setDetails(false)} data-id="element-2127" data-mp="3flvqfz15isgm" />
    </>;
}